import React, { useMemo, useState } from 'react'

const COLOR_THEMES = {
  primary: {
    icon: 'text-primary',
    badge: 'bg-primary/10 text-primary',
    selectedRow: 'bg-primary-fixed/60 text-primary',
    checkbox: 'text-primary focus:ring-primary',
    btnAll: 'text-primary hover:bg-primary/10',
  },
  secondary: {
    icon: 'text-secondary',
    badge: 'bg-secondary/10 text-secondary',
    selectedRow: 'bg-secondary-fixed/60 text-secondary',
    checkbox: 'text-secondary focus:ring-secondary',
    btnAll: 'text-secondary hover:bg-secondary/10',
  },
  tertiary: {
    icon: 'text-tertiary',
    badge: 'bg-tertiary/10 text-tertiary',
    selectedRow: 'bg-tertiary-fixed/60 text-tertiary',
    checkbox: 'text-tertiary focus:ring-tertiary',
    btnAll: 'text-tertiary hover:bg-tertiary/10',
  },
  accent: {
    icon: 'text-indigo-600',
    badge: 'bg-indigo-50 text-indigo-700',
    selectedRow: 'bg-indigo-50/80 text-indigo-900',
    checkbox: 'text-indigo-600 focus:ring-indigo-500',
    btnAll: 'text-indigo-600 hover:bg-indigo-50',
  },
  emerald: {
    icon: 'text-emerald-600',
    badge: 'bg-emerald-50 text-emerald-700',
    selectedRow: 'bg-emerald-50/80 text-emerald-900',
    checkbox: 'text-emerald-600 focus:ring-emerald-500',
    btnAll: 'text-emerald-600 hover:bg-emerald-50',
  },
  amber: {
    icon: 'text-amber-600',
    badge: 'bg-amber-50 text-amber-700',
    selectedRow: 'bg-amber-50/80 text-amber-900',
    checkbox: 'text-amber-600 focus:ring-amber-500',
    btnAll: 'text-amber-600 hover:bg-amber-50',
  },
}

export default function EntityMultiSelectPanel({
  title,
  icon = 'checklist',
  required = false,
  selectedIds = [],
  options = [],
  onToggle,
  onSelectAll,
  onClearAll,
  isLoading = false,
  disabled = false,
  disabledMessage = 'Select prerequisite options first',
  emptyMessage = 'No items found',
  searchPlaceholder,
  colorTheme = 'primary',
  maxHeight = 'max-h-60 min-h-[160px]',
  className = '',
}) {
  const [search, setSearch] = useState('')
  const theme = COLOR_THEMES[colorTheme] || COLOR_THEMES.primary

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter((item) => {
      const nameMatch = item.name && String(item.name).toLowerCase().includes(q)
      const codeMatch = item.code && String(item.code).toLowerCase().includes(q)
      const subMatch = item.subtitle && String(item.subtitle).toLowerCase().includes(q)
      const compMatch = item.companyName && String(item.companyName).toLowerCase().includes(q)
      return Boolean(nameMatch || codeMatch || subMatch || compMatch)
    })
  }, [search, options])

  const selectedCount = selectedIds.length
  const placeholder = searchPlaceholder || `Search ${title.toLowerCase()}...`

  return (
    <div
      className={`flex flex-col rounded-xl border border-outline-variant/30 bg-surface-container-low/40 p-4 transition-all duration-200 ${className}`}
    >
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-1 pb-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`material-symbols-outlined text-base flex-shrink-0 ${theme.icon}`}>
            {icon}
          </span>
          <span className="text-xs font-black uppercase tracking-wider text-on-surface whitespace-nowrap">
            {title}
          </span>
          {required ? (
            <span className="text-error font-bold text-sm leading-none flex-shrink-0" title="Required field">
              *
            </span>
          ) : null}
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold flex-shrink-0 ${theme.badge}`}>
            {selectedCount}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={onSelectAll}
            disabled={disabled || isLoading || options.length === 0}
            className={`rounded px-1.5 py-0.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${theme.btnAll}`}
          >
            All
          </button>
          <span className="text-xs text-outline/40 select-none">|</span>
          <button
            type="button"
            onClick={onClearAll}
            disabled={disabled || isLoading || selectedCount === 0}
            className="rounded px-1.5 py-0.5 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Search Filter Box */}
      <div className="relative mb-2.5">
        <span className="material-symbols-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-outline">
          search
        </span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled || isLoading}
          placeholder={disabled ? 'Locked' : placeholder}
          className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest py-2 pl-8 pr-7 text-xs text-on-surface outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60 placeholder:text-outline/70"
        />
        {search ? (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-outline hover:text-on-surface p-0.5"
            aria-label="Clear search"
          >
            <span className="material-symbols-outlined text-sm">cancel</span>
          </button>
        ) : null}
      </div>

      {/* Scrollable Item Container */}
      <div
        className={`flex-1 space-y-1 overflow-y-auto rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-1.5 shadow-inner ${maxHeight}`}
      >
        {disabled ? (
          <div className="flex h-full min-h-[140px] flex-col items-center justify-center gap-1.5 px-3 py-6 text-center text-xs text-on-surface-variant">
            <span className="material-symbols-outlined text-2xl text-outline/80">info</span>
            <span className="max-w-[220px] leading-relaxed">{disabledMessage}</span>
          </div>
        ) : isLoading ? (
          <div className="flex h-full min-h-[140px] flex-col items-center justify-center gap-2 py-6 text-center text-xs text-on-surface-variant">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span>Loading {title.toLowerCase()}...</span>
          </div>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((item) => {
            const isChecked = selectedIds.includes(Number(item.id))
            const subtitleText = item.subtitle || item.companyName || ''
            return (
              <label
                key={`item-${item.id}`}
                className={`flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-2 transition-colors ${
                  isChecked
                    ? `${theme.selectedRow} font-medium`
                    : 'text-on-surface hover:bg-surface-container-low'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle && onToggle(item.id)}
                    className={`h-4 w-4 rounded border-outline-variant transition-colors flex-shrink-0 ${theme.checkbox}`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold break-words leading-snug" title={item.name}>
                      {item.name}
                    </p>
                    {subtitleText ? (
                      <p className="text-[10px] text-on-surface-variant/80 truncate mt-0.5">
                        {subtitleText}
                      </p>
                    ) : null}
                  </div>
                </div>
              </label>
            )
          })
        ) : (
          <div className="flex h-full min-h-[140px] flex-col items-center justify-center gap-1 py-6 text-center text-xs text-on-surface-variant">
            <span className="material-symbols-outlined text-xl text-outline/60">search_off</span>
            <span>{search ? `No matching ${title.toLowerCase()}` : emptyMessage}</span>
          </div>
        )}
      </div>
    </div>
  )
}
